import axios from 'axios';

type RuntimeConfig = {
  API_BASE_URL?: string;
  YM_COUNTER_ID?: string;
  APP_VERSION?: string;
};

function getRuntimeConfig(): RuntimeConfig | undefined {
  try {
    return (window as any).__APP_CONFIG__ as RuntimeConfig | undefined;
  } catch {
    return undefined;
  }
}

export const API_BASE_URL =
  getRuntimeConfig()?.API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  // Safe default for production; overridden by config.js or REACT_APP_API_URL at build time
  'https://api.luemen.ru';

export const YM_COUNTER_ID = getRuntimeConfig()?.YM_COUNTER_ID || process.env.REACT_APP_YM_COUNTER_ID || '';
export const APP_VERSION = getRuntimeConfig()?.APP_VERSION || process.env.REACT_APP_APP_VERSION || 'dev';

export const apiUrl = (path: string) =>
  `${API_BASE_URL.replace(/\/$/, '')}${path.startsWith('/') ? '' : '/'}${path}`;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Error interceptor for detailed logging + session expiry redirect
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('🔴 API Error Interceptor:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
      data: error.response?.data,
      headers: error.config?.headers
    });
    if (error.response?.status === 401 && !error.config?.url?.includes('/api/auth/login')) {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('viewMode');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
      }
    }
    return Promise.reject(error);
  }
);

// Analytics API
export const analyticsAPI = {
  overview: async (start: string, end: string) => {
    const response = await api.get(`/api/analytics/overview?start=${start}&end=${end}`);
    return response.data;
  },
};

export const reportsAPI = {
  employees: async (params?: { customerId?: number }) => {
    const response = await api.get('/api/reports/employees', { params });
    return response.data as {
      employees: Array<{ id: number; fullName: string; username: string }>;
    };
  },
  employeeOrders: async (params: {
    start: string;
    end: string;
    customerId?: number;
    employeeId?: number;
  }) => {
    const response = await api.get('/api/reports/employee-orders', { params });
    return response.data;
  },
  limitUsage: async (params: { start: string; end: string; customerId?: number }) => {
    const response = await api.get('/api/reports/limit-usage', { params });
    return response.data;
  },
  orderTrace: async (params: { date: string; customerId?: number; employeeId?: number }) => {
    const response = await api.get('/api/reports/order-trace', { params });
    return response.data;
  },
};

// Auth API
export const authAPI = {
  login: async (username: string, password: string) => {
    const response = await api.post('/api/auth/login', { username, password });
    return response.data;
  },
  
  register: async (data: { email: string; username: string; password: string; role: string }) => {
    const response = await api.post('/api/auth/register', data);
    return response.data;
  },
  
  me: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },
  forgotPassword: async (email: string) => {
    const response = await api.post('/api/auth/forgot-password', { email });
    return response.data;
  },
  resetPassword: async (email: string, code: string, newPassword: string) => {
    const response = await api.post('/api/auth/reset-password', { 
      email, 
      code, 
      newPassword 
    });
    return response.data;
  },
  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await api.post('/api/auth/change-password', { currentPassword, newPassword });
    return response.data;
  },
  forgotUsername: async (email: string) => {
    const response = await api.post('/api/auth/forgot-username', { email });
    return response.data;
  }
};

/** Профиль: ФИО/телефон/компания видны заказчику в разделе «Мои поставщики» */
export const usersProfileAPI = {
  update: async (data: {
    email?: string;
    username?: string;
    fullName?: string | null;
    phone?: string | null;
    companyName?: string | null;
    inn?: string | null;
  }) => {
    const response = await api.put('/api/users/profile', data);
    return response.data;
  }
};

// Customer employees API
export const customerEmployeesAPI = {
  getEmployees: async () => {
    const response = await api.get('/api/auth/customer/employees');
    return response.data;
  },
  getApprovals: async () => {
    const response = await api.get('/api/auth/approvals');
    return response.data;
  },
  approve: async (id: number) => {
    const response = await api.post(`/api/auth/approvals/${id}/approve`, {});
    return response.data;
  },
  reject: async (id: number, comment: string) => {
    const response = await api.post(`/api/auth/approvals/${id}/reject`, { comment });
    return response.data;
  },
  revert: async (id: number, action: 'pending' | 'approved') => {
    const response = await api.post(`/api/auth/approvals/${id}/revert`, { action });
    return response.data;
  },
  updateEmployee: async (
    id: number,
    data: {
      fullName?: string;
      phone?: string;
      email?: string;
    }
  ) => {
    const response = await api.put(`/api/auth/customer/employees/${id}`, data);
    return response.data;
  },
  setRole: async (id: number, role: 'employee' | 'customer_manager') => {
    const response = await api.put(`/api/auth/customer/employees/${id}/role`, { role });
    return response.data;
  },
  setArchived: async (id: number, archived: boolean) => {
    const response = await api.put(`/api/auth/customer/employees/${id}/archive`, { archived });
    return response.data;
  },
  setLimit: async (
    id: number,
    data: {
      dailyLimit?: number | null;
      monthlyLimit?: number | null;
    }
  ) => {
    const response = await api.put(`/api/auth/customer/employees/${id}/limits`, data);
    return response.data;
  }
};

export const worktimeLimitsAPI = {
  getSettings: async () => {
    const response = await api.get('/api/worktime-limits/settings');
    return response.data;
  },
  updateSettings: async (data: { automationEnabled?: boolean; worktimeBaseUrl?: string | null }) => {
    const response = await api.put('/api/worktime-limits/settings', data);
    return response.data;
  },
  updateTiers: async (tiers: Array<{ tier: number; dailyLimit: number; monthlyLimit: number }>) => {
    const response = await api.put('/api/worktime-limits/tiers', { tiers });
    return response.data;
  },
  getLinks: async () => {
    const response = await api.get('/api/worktime-limits/links');
    return response.data;
  },
  updateLink: async (employeeId: number, worktimeUserId: number | null) => {
    const response = await api.put(`/api/worktime-limits/links/${employeeId}`, { worktimeUserId });
    return response.data;
  },
  syncNow: async () => {
    const response = await api.post('/api/worktime-limits/sync');
    return response.data;
  },
};

// Supplier delivery team API (edit approved delivery users)
export const supplierDeliveryTeamAPI = {
  updateDriver: async (
    id: number,
    data: {
      fullName?: string;
      phone?: string;
      email?: string;
    }
  ) => {
    const response = await api.put(`/api/auth/supplier/delivery/${id}`, data);
    return response.data;
  },
  deleteDriver: async (id: number) => {
    const response = await api.delete(`/api/auth/supplier/delivery/${id}`);
    return response.data;
  },
};

// Delivery addresses API
export const deliveryAddressesAPI = {
  list: async () => {
    const response = await api.get('/api/delivery-addresses');
    return response.data;
  },
  create: async (data: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    postalCode?: string;
    notes?: string;
    isDefault?: boolean;
  }) => {
    const response = await api.post('/api/delivery-addresses', data);
    return response.data;
  },
  update: async (
    id: number,
    data: {
      addressLine1: string;
      addressLine2?: string;
      city: string;
      postalCode?: string;
      notes?: string;
      isDefault?: boolean;
    }
  ) => {
    const response = await api.put(`/api/delivery-addresses/${id}`, data);
    return response.data;
  },
  remove: async (id: number) => {
    const response = await api.delete(`/api/delivery-addresses/${id}`);
    return response.data;
  }
};

// Delivery ratings API
export const deliveryRatingsAPI = {
  getDrivers: async (scope?: 'supplier' | 'customer' | 'admin') => {
    const response = await api.get('/api/delivery-ratings/drivers', {
      params: scope ? { scope } : undefined
    });
    return response.data;
  },
  rateDriver: async (data: { orderId: number; rating: number; comment?: string }) => {
    const response = await api.post('/api/delivery-ratings', data);
    return response.data;
  }
};

// Menu API
export const menuAPI = {
  getItems: async (params?: { supplierId?: number; category?: string; isAvailable?: boolean }) => {
    const response = await api.get('/api/menu/items', { params });
    return response.data;
  },
  
  getItem: async (id: number) => {
    const response = await api.get(`/api/menu/items/${id}`);
    return response.data;
  },
  
  createItem: async (data: any) => {
    const response = await api.post('/api/menu/items', data);
    return response.data;
  },
  
  updateItem: async (id: number, data: any) => {
    const response = await api.put(`/api/menu/items/${id}`, data);
    return response.data;
  },
  
  deleteItem: async (id: number) => {
    const response = await api.delete(`/api/menu/items/${id}`);
    return response.data;
  },

  bulkDeleteItems: async (ids: number[]) => {
    const response = await api.post('/api/menu/items/bulk-delete', { ids });
    return response.data as { success: boolean; deletedCount: number; archivedCount?: number; message?: string };
  },

  findDuplicateItems: async () => {
    const response = await api.get('/api/menu/items/duplicates');
    return response.data as {
      groupCount: number;
      duplicateCount: number;
      groups: Array<{
        key: string;
        keepId: number;
        duplicateIds: number[];
        items: Array<{
          id: number;
          name: string;
          category: string;
          price: number;
          isAvailable: boolean;
          createdAt: string;
        }>;
      }>;
    };
  },

  removeDuplicateItems: async (params?: { dryRun?: boolean }) => {
    const response = await api.post('/api/menu/items/remove-duplicates', {}, { params });
    return response.data as {
      dryRun: boolean;
      groupCount: number;
      removedCount?: number;
      removedIds?: number[];
      wouldRemoveCount?: number;
      skipped?: Array<{ id: number; name: string; reason: string }>;
    };
  },
  
  uploadImage: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await api.post(`/api/menu/items/${id}/upload-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  deleteImage: async (id: number) => {
    const response = await api.delete(`/api/menu/items/${id}/image`);
    return response.data;
  },
  
  getWeeklyMenus: async (params?: { supplierId?: number; isActive?: boolean; startDate?: string; endDate?: string }) => {
    const response = await api.get('/api/menu/weekly', { params });
    return response.data;
  },
  saveWeeklyMenu: async (data: {
    id?: number;
    supplierId?: number;
    weekStartDate: string;
    weekEndDate?: string;
    items: Array<{
      menuItemId: number;
      dayOfWeek?: string | null;
      specificDate?: string | null;
      availableQuantity?: number;
      isAvailable?: boolean;
    }>;
  }) => {
    const response = await api.post('/api/menu/weekly', data);
    return response.data;
  },
  deleteWeeklyMenu: async (id: number) => {
    const response = await api.delete(`/api/menu/weekly/${id}`);
    return response.data;
  },

  importItemsExcel: async (file: File, params?: { dryRun?: boolean; mode?: 'upsert' | 'create_only' }) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/api/menu/items/import-excel', formData, {
      params,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  downloadImportTemplate: async () => {
    const response = await api.get('/api/menu/items/import-excel/template', {
      responseType: 'blob'
    });
    return response.data as Blob;
  },

  importWeeklyMenuExcel: async (file: File, params?: { dryRun?: boolean; mode?: 'replace_days' | 'add' }) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/api/menu/weekly/import-excel', formData, {
      params,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  downloadWeeklyMenuImportTemplate: async () => {
    const response = await api.get('/api/menu/weekly/import-excel/template', {
      responseType: 'blob'
    });
    return response.data as Blob;
  }
};

// Combos API (supplier/admin)
export const combosAPI = {
  getDay: async (date: string, params?: { supplierId?: number }) => {
    const response = await api.get(`/api/combos/day/${date}`, { params });
    return response.data;
  },
  setDay: async (date: string, data: { combosOnly: boolean; supplierId?: number }) => {
    const response = await api.put(`/api/combos/day/${date}`, data);
    return response.data;
  },
  createCombo: async (date: string, data: any) => {
    const response = await api.post(`/api/combos/day/${date}/combos`, data);
    return response.data;
  },
  updateCombo: async (id: number, data: any) => {
    const response = await api.put(`/api/combos/combos/${id}`, data);
    return response.data;
  },
  deleteCombo: async (id: number) => {
    const response = await api.delete(`/api/combos/combos/${id}`);
    return response.data;
  },
  copy: async (data: {
    sourceDate: string;
    targetStart: string;
    targetEnd: string;
    supplierId?: number;
    mode?: 'replace' | 'add';
    copyCombosOnly?: boolean;
  }) => {
    const response = await api.post('/api/combos/copy', data);
    return response.data;
  }
};

// Orders API
export const ordersAPI = {
  getOrders: async (params?: { customerId?: number; supplierId?: number; status?: string }) => {
    const response = await api.get('/api/orders', { params });
    return response.data;
  },
  
  getOrder: async (id: number) => {
    const response = await api.get(`/api/orders/${id}`);
    return response.data;
  },
  
  createOrder: async (data: any) => {
    const response = await api.post('/api/orders', data);
    return response.data;
  },
  
  updateOrderStatus: async (id: number, status: string) => {
    const response = await api.put(`/api/orders/${id}/status`, { status });
    return response.data;
  },

  confirmOrderReceipt: async (id: number) => {
    const response = await api.post(`/api/orders/${id}/confirm-receipt`);
    return response.data;
  },

  agreeOrderCancel: async (id: number, reason: string) => {
    const response = await api.post(`/api/orders/${id}/cancel-agree`, { reason });
    return response.data;
  },
};

// Support API
export const supportAPI = {
  getTickets: async (params?: { status?: string; archived?: boolean }) => {
    const response = await api.get('/api/support/tickets', { params });
    return response.data;
  },
  
  createTicket: async (data: { subject: string; message: string; priority?: string }) => {
    const response = await api.post('/api/support/tickets', data);
    return response.data;
  },
  
  replyTicket: async (id: number, message: string) => {
    const response = await api.post(`/api/support/tickets/${id}/reply`, { message });
    return response.data;
  },
  
  closeTicket: async (id: number) => {
    const response = await api.put(`/api/support/tickets/${id}/close`, {});
    return response.data;
  },
};

// Users API
export const usersAPI = {
  getUsers: async (params?: { role?: string; customerId?: number }) => {
    const response = await api.get('/api/users', { params });
    return response.data;
  },
  
  getUser: async (id: number) => {
    const response = await api.get(`/api/users/${id}`);
    return response.data;
  },
  
  updateUser: async (id: number, data: any) => {
    const response = await api.put(`/api/users/${id}`, data);
    return response.data;
  },

  deleteUser: async (id: number) => {
    const response = await api.delete(`/api/auth/users/${id}`);
    return response.data;
  },
  restoreUser: async (id: number) => {
    const response = await api.post(`/api/auth/users/${id}/restore`, {});
    return response.data;
  },
};

// Documents API
export const documentsAPI = {
  getDocuments: async () => {
    const response = await api.get('/api/auth/documents');
    return response.data;
  },

  getDocumentVersions: async (key: string) => {
    console.log('🌐 API call: getDocumentVersions for key:', key);
    const response = await api.get(`/api/auth/documents/${key}/versions`);
    console.log('📡 API response:', response.data);
    return response.data;
  },

  createDocument: async (data: { title: string; key: string; content: string; version: string; roles: string[] }) => {
    const response = await api.post('/api/auth/documents', data);
    return response.data;
  },

  updateDocument: async (id: number, data: { title: string; content: string; version: string; roles?: string[] }) => {
    const response = await api.put(`/api/auth/documents/${id}`, data);
    return response.data;
  },

  getDocumentAcceptances: async () => {
    const response = await api.get('/api/auth/document-acceptances');
    return response.data;
  },
};

// Web Push API (employee)
export const pushAPI = {
  getVapidPublicKey: async () => {
    const response = await api.get('/api/push/vapid-public-key');
    return response.data as { publicKey: string };
  },
  getSettings: async () => {
    const response = await api.get('/api/push/settings');
    return response.data as { enabled: boolean; time: string };
  },
  updateSettings: async (data: { enabled?: boolean; time?: string }) => {
    const response = await api.put('/api/push/settings', data);
    return response.data as { enabled: boolean; time: string };
  },
  subscribe: async (subscription: any) => {
    const response = await api.post('/api/push/subscribe', { subscription });
    return response.data as { success: boolean };
  },
  unsubscribe: async (endpoint: string) => {
    const response = await api.post('/api/push/unsubscribe', { endpoint });
    return response.data as { success: boolean };
  },
  test: async () => {
    const response = await api.post('/api/push/test', {});
    return response.data as { success: boolean; sent: number; total: number };
  }
};

// Link Requests API
export const linkRequestsAPI = {
  getSupplierRequests: async (params?: { status?: string }) => {
    const response = await api.get('/api/link-requests/supplier', { params });
    return response.data;
  },
  
  getCustomerRequests: async (params?: { status?: string }) => {
    const response = await api.get('/api/link-requests/customer', { params });
    return response.data;
  },
  
  approveRequest: async (id: number) => {
    const response = await api.put(`/api/link-requests/${id}/approve`);
    return response.data;
  },
  
  rejectRequest: async (id: number, notes?: string) => {
    const response = await api.put(`/api/link-requests/${id}/reject`, { notes });
    return response.data;
  },
  
  createRequest: async (data: { supplierId: number; notes?: string; message?: string }) => {
    const response = await api.post('/api/link-requests', data);
    return response.data;
  },

  // Новые методы для двусторонних заявок
  createSupplierToCustomerRequest: async (data: { customerId: number; notes?: string; message?: string }) => {
    const response = await api.post('/api/link-requests/supplier-to-customer', data);
    return response.data;
  },

  searchCustomers: async (params: { inn?: string; companyName?: string }) => {
    const response = await api.get('/api/link-requests/search-customers', { params });
    return response.data;
  },

  getOrderingMenu: async () => {
    const response = await api.get('/api/link-requests/ordering-menu');
    return response.data as {
      orderingSupplierId: number | null;
      suppliers: Array<{ id: number; name: string }>;
      canSwitch: boolean;
    };
  },

  getOrderingMenuPendingCarts: async (supplierId: number) => {
    const response = await api.get('/api/link-requests/ordering-menu/pending-carts', {
      params: { supplierId },
    });
    return response.data as {
      willAnnul: boolean;
      employeeCount: number;
      itemCount: number;
    };
  },

  setOrderingMenu: async (supplierId: number) => {
    const response = await api.put('/api/link-requests/ordering-menu', { supplierId });
    return response.data as {
      success: boolean;
      orderingSupplierId: number;
      supplierName: string;
      annulled: { employeeCount: number; itemCount: number };
    };
  },

  getCartNotices: async () => {
    const response = await api.get('/api/daily-menu/cart-notices');
    return response.data as {
      notices: Array<{ id: number; message: string; createdAt: string; kind: string }>;
    };
  },

  acknowledgeCartNotices: async (ids?: number[]) => {
    const response = await api.post('/api/daily-menu/cart-notices/acknowledge', { ids });
    return response.data;
  },

  searchSuppliers: async (params: { inn?: string; companyName?: string }) => {
    const response = await api.get('/api/link-requests/search-suppliers', { params });
    return response.data;
  },

  setDiscount: async (id: number, discountPercent?: number) => {
    const response = await api.put(`/api/link-requests/${id}/discount`, { discountPercent });
    return response.data;
  },

  setEmergencyPermission: async (id: number, allow: boolean) => {
    const response = await api.put(`/api/link-requests/${id}/emergency-permission`, { allow });
    return response.data;
  },

  toggleEmergencyOrder: async (id: number, enabled: boolean, durationHours?: number) => {
    const response = await api.post(`/api/link-requests/${id}/emergency-toggle`, { enabled, durationHours });
    return response.data;
  },

  archiveRequest: async (id: number) => {
    const response = await api.put(`/api/link-requests/${id}/archive`);
    return response.data;
  },
};

// Policy Documents API
export const policyDocumentsAPI = {
  getForRole: async (role: string) => {
    const response = await api.get(`/api/policy-documents/for-role/${role}`);
    return response.data;
  },

  checkAcceptance: async (role: string) => {
    const response = await api.get(`/api/auth/check-acceptance/${role}`);
    return response.data;
  },

  acceptDocument: async (documentId: number, version: string) => {
    const response = await api.post('/api/policy-documents/accept', {
      documentId,
      version,
      ipAddress: window.location.hostname,
      userAgent: navigator.userAgent
    });
    return response.data;
  },

  getMyAcceptances: async () => {
    const response = await api.get('/api/policy-documents/my-acceptances');
    return response.data;
  }
};

// Suppliers API
export const suppliersAPI = {
  lookupCompany: async (inn: string) => {
    const response = await api.get(`/api/suppliers/lookup/${inn}`);
    return response.data;
  },

  getMyOrderRules: async () => {
    const response = await api.get('/api/suppliers/me/order-rules');
    return response.data as { employeeCancelDeadlineTime: string | null; orderDeadline: string };
  },

  updateMyOrderRules: async (data: { employeeCancelDeadlineTime: string | null; orderDeadline?: string | null }) => {
    const response = await api.put('/api/suppliers/me/order-rules', data);
    return response.data as { employeeCancelDeadlineTime: string | null; orderDeadline: string };
  }
};

// Dish Ratings API
export const dishRatingsAPI = {
  getMenuItemRatings: async (menuItemId: number) => {
    const response = await api.get(`/api/dish-ratings/menu-item/${menuItemId}`);
    return response.data;
  },

  createRating: async (data: {
    menuItemId: number;
    orderId?: number;
    rating: number;
    review?: string;
    isAnonymous?: boolean;
    companyName?: string;
  }) => {
    const response = await api.post('/api/dish-ratings', data);
    return response.data;
  },

  getSupplierAnalytics: async (supplierId: number) => {
    const response = await api.get(`/api/dish-ratings/analytics/supplier/${supplierId}`);
    return response.data;
  },

  getEscalations: async () => {
    const response = await api.get('/api/dish-ratings/escalations');
    return response.data;
  },

  updateEscalation: async (ratingId: number, escalationLevel: string) => {
    const response = await api.put(`/api/dish-ratings/${ratingId}/escalation`, { escalationLevel });
    return response.data;
  }
};

// Thermal Labels API
export const thermalLabelsAPI = {
  getOrderLabels: async (orderId: number) => {
    const response = await api.get(`/api/thermal-labels/order/${orderId}`);
    return response.data;
  },

  getDishLabels: async (orderItemId: number) => {
    const response = await api.get(`/api/thermal-labels/dish/${orderItemId}`);
    return response.data;
  },

  getComboLabels: async (orderComboItemId: number) => {
    const response = await api.get(`/api/thermal-labels/combo/${orderComboItemId}`);
    return response.data;
  },

  scanDish: async (orderItemId: number) => {
    const response = await api.post(`/api/thermal-labels/scan/${orderItemId}`);
    return response.data;
  },

  scanCombo: async (orderComboItemId: number) => {
    const response = await api.post(`/api/thermal-labels/scan-combo/${orderComboItemId}`);
    return response.data;
  },

  getScanStats: async (orderId: number) => {
    const response = await api.get(`/api/thermal-labels/scan-stats/${orderId}`);
    return response.data;
  }
};

export const orderIssuesAPI = {
  list: async () => {
    const response = await api.get('/api/order-issues');
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get(`/api/order-issues/${id}`);
    return response.data;
  },
  eligibleOrders: async () => {
    const response = await api.get('/api/order-issues/eligible-orders');
    return response.data;
  },
  openThread: async (data: {
    orderId: number;
    subjectEmployeeId?: number;
    subjectMenuItemId?: number;
    message: string;
    discrepancyType?: string;
  }) => {
    const response = await api.post('/api/order-issues/open', data);
    return response.data as { thread: any; created: boolean };
  },
  createJson: async (data: { orderId: number; discrepancyType?: string; body: string }) => {
    const response = await api.post('/api/order-issues', data);
    return response.data;
  },
  create: async (formData: FormData) => {
    const response = await api.post('/api/order-issues', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  replyJson: async (id: number, body: string) => {
    const response = await api.post(`/api/order-issues/${id}/messages`, { body });
    return response.data;
  },
  reply: async (id: number, formData: FormData) => {
    const response = await api.post(`/api/order-issues/${id}/messages`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  setStatus: async (id: number, status: 'open' | 'resolved') => {
    const response = await api.patch(`/api/order-issues/${id}/status`, { status });
    return response.data;
  },
  getSettings: async () => {
    const response = await api.get('/api/order-issues/settings');
    return response.data;
  },
  putSettings: async (data: { showOrderIssuesToAllEmployees: boolean; contactUserIds: number[] }) => {
    const response = await api.put('/api/order-issues/settings', data);
    return response.data;
  }
};

export const orderCorrectionsAPI = {
  list: async (orderId: number) => {
    const response = await api.get('/api/order-corrections', { params: { orderId } });
    return response.data;
  },
  listPending: async () => {
    const response = await api.get('/api/order-corrections/pending');
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get(`/api/order-corrections/${id}`);
    return response.data;
  },
  propose: async (data: {
    orderId: number;
    correctionAmount: number;
    reason: string;
    discrepancyType?: string;
    subjectEmployeeId?: number;
    subjectMenuItemId?: number;
    threadId?: number;
  }) => {
    const response = await api.post('/api/order-corrections', data);
    return response.data;
  },
  approve: async (id: number) => {
    const response = await api.post(`/api/order-corrections/${id}/approve`);
    return response.data;
  },
  reject: async (id: number, reason?: string) => {
    const response = await api.post(`/api/order-corrections/${id}/reject`, { reason });
    return response.data;
  }
};

export default api;

